// ════════════════════════════════════════════════════════════
//  seals.js — THE SEAL GRAMMAR (placeholder until Naew)
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-20: "the seals on the floor the patterns maybe should
//  be different not just the color when they're from another school
//  and/or source … create continuity so seeing these spells etc mean the
//  same thing. But don't go too crazy with it because we are actually
//  creating a 'Naew' language using seals etc so we will replace seals
//  proper later."
//
//  So: ONE file decides what every seal looks like, and Naew replaces
//  this file (the draw functions) without touching anything that calls
//  it. Nothing else in the game paints a seal.
//
//  A seal says three things, always in the same place:
//
//   1. THE RIM — what KIND of Art it is (and, for a Tome, its SCHOOL)
//        Impera ........ one ring, twelve ticks
//        U'hlti ........ three rings (the ladder's top rung)
//        Tome · Nihil .. a soft halo ring, nothing on it (it erases)
//        Tome · Omnia .. a ring with eight horns curling out (it creates, and Ruins)
//        Tome · Construct  a square foundation with corner stones
//        Tome · Treasure   a cut gem: two octagons and their facets
//        Tome · Blood Contract  a ring of chain links
//
//   2. THE FOUR — the Aenergies it WORKS ON (what it does, never what it costs —
//      every Art costs Khao, so the price would light Khao on all of them), at their CANON
//      places (Kompendium, "The Four Colours and Their Positions"):
//        Khao top (Void Purple) · Anima bottom (Celestial Gold)
//        Lyf left (Blue Star Ice) · Lyt right (Sunfire Red)
//      A lit point = this Art uses that Aenergy; a dim hollow point = it
//      doesn't. A U'hlti lights all four ("all four in harmony").
//      The Four are painted in their own colours and always face the
//      viewer (Khao at the top of the screen), never spun.
//
//   3. THE HEART — whom it touches
//        on yourself .... a dot in a ring
//        aimed at an area  a triangle
//        the whole field . an eight-point star
//
//  The rim + heart are white and take the CASTER's colour (who); the Four
//  carry their own canon colours (what). Colour = who, pattern = what.
// ════════════════════════════════════════════════════════════
import { ARTS } from "./arts.js";

export const FOUR = {
  khao:  { core: "#A855F7", deep: "#4C1D95", glow: "#DDB8FF", at: [0, -1], label: "Khao" },    // top
  anima: { core: "#F2B134", deep: "#7A4E00", glow: "#FFDC8A", at: [0, 1], label: "Anima" },    // bottom
  lyf:   { core: "#4CC9F0", deep: "#0A4A66", glow: "#B3ECFF", at: [-1, 0], label: "Lyf" },     // left
  lyt:   { core: "#FF4D3D", deep: "#7A1200", glow: "#FF9E8F", at: [1, 0], label: "Lyt" },      // right
};
export const RIMS = ["impera", "uhlti", "nihil", "omnia", "construct", "treasure", "contract"];
export const RIM_LABEL = { impera: "Impera", uhlti: "U'hlti", nihil: "Tome · Nihil", omnia: "Tome · Omnia",
  construct: "Tome · Construct", treasure: "Tome · Treasure", contract: "Tome · Blood Contract" };
const SCHOOL_RIM = { Nihil: "nihil", Omnia: "omnia", Construct: "construct", Treasure: "treasure", "Blood Contract": "contract" };

// ── which of the Four an Art uses (read off its own row; no list to keep) ─
const USES = {
  khao: ["khao", "foeKhao", "maxKhao", "orKhao", "coin", "ruinNonBeings", "ruinBenches", "phaseEvery"],   // Ruin is Khao injected (Kompendium, Nihil & Omnia)
  anima: ["dimAnima", "lytToAnima", "anima", "reviveIlk", "lyfOrAnima", "dimAnimaIfIntangible"],
  lyt: ["lytHit", "lytToAnima", "lyt", "lytCharges", "pierce", "ruinOverLyt", "chargeEvery"],
  lyf: ["dimLyf", "maxLyf", "lyfOrAnima"],
};
const STATUS_USES = { piercing: "lyt", scalpel: "lyt", reprisal: "lyt", hope: "anima", absorb: "anima", destined: "anima", fate: "khao",
  bulwark: "lyf", warded: "lyf", nullLyt: "lyf", tomeproof: "lyf", intangible: "khao", nightmare: "anima" };

const CACHE = new Map();
export function sealOf(artId) {
  if (CACHE.has(artId)) return CACHE.get(artId);
  const a = ARTS[artId];
  const s = { rim: "impera", heart: "self", four: { khao: false, anima: false, lyf: false, lyt: false } };
  if (a) {
    s.rim = a.kind === "uhlti" ? "uhlti" : a.kind === "tome" ? (SCHOOL_RIM[a.school] || "nihil") : "impera";
    s.heart = a.target === "area" ? "area" : a.target === "global" ? "global" : "self";
    // NOT the price: every Art costs Khao, so a lit point means what the Art DOES, read off its effects
    const look = (o) => {
      if (!o || typeof o !== "object") return;
      for (const [k, v] of Object.entries(o)) {
        for (const e of Object.keys(USES)) if (USES[e].includes(k)) s.four[e] = true;
        if (k === "status" && STATUS_USES[v]) s.four[STATUS_USES[v]] = true;
        if (k === "give" || k === "treasure" || k === "construct") look(v);   // what a Tome gives / holds counts too
        if ((k === "win" || k === "lose") && Array.isArray(v)) v.forEach(look);
      }
    };
    a.effects.forEach(look);
    if (a.kind === "uhlti") s.four = { khao: true, anima: true, lyf: true, lyt: true };
  }
  s.key = s.rim + "_" + s.heart + "_" + ["khao", "anima", "lyf", "lyt"].map((k) => (s.four[k] ? 1 : 0)).join("");
  CACHE.set(artId, s);
  return s;
}

// ════════════════════════════════════════════════════════════
//  THE PAINT — canvas only, so a test (or Naew's tool) can draw it anywhere
// ════════════════════════════════════════════════════════════
const TAU = Math.PI * 2;
const line = (g, w, a = 0.95) => { g.lineWidth = w; g.strokeStyle = `rgba(255,255,255,${a})`; };
const ring = (g, r, w, a) => { line(g, w, a); g.beginPath(); g.arc(0, 0, r, 0, TAU); g.stroke(); };
const poly = (g, n, r, rot) => { g.beginPath(); for (let i = 0; i <= n; i++) { const t = rot + i / n * TAU; i ? g.lineTo(Math.cos(t) * r, Math.sin(t) * r) : g.moveTo(Math.cos(t) * r, Math.sin(t) * r); } };

const RIM_DRAW = {
  impera(g, R) {
    ring(g, R, 4); ring(g, R - 16, 1.8, 0.8);
    line(g, 2.4);
    for (let i = 0; i < 12; i++) { const t = i / 12 * TAU; g.beginPath(); g.moveTo(Math.cos(t) * (R - 14), Math.sin(t) * (R - 14)); g.lineTo(Math.cos(t) * (R - 2), Math.sin(t) * (R - 2)); g.stroke(); }
  },
  uhlti(g, R) {
    ring(g, R, 4); ring(g, R - 13, 2.4, 0.9); ring(g, R - 26, 1.6, 0.75);
    line(g, 1.6, 0.8);
    for (let i = 0; i < 24; i++) { const t = i / 24 * TAU; g.beginPath(); g.moveTo(Math.cos(t) * (R - 13), Math.sin(t) * (R - 13)); g.lineTo(Math.cos(t) * (R - 26), Math.sin(t) * (R - 26)); g.stroke(); }
  },
  nihil(g, R) {                  // the halo: soft and unbroken — it takes away, adds nothing
    const gr = g.createRadialGradient(0, 0, R - 20, 0, 0, R);
    gr.addColorStop(0, "rgba(255,255,255,0)"); gr.addColorStop(0.6, "rgba(255,255,255,0.55)"); gr.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = gr; g.beginPath(); g.arc(0, 0, R, 0, TAU); g.fill();
    ring(g, R - 8, 3); ring(g, R - 24, 1.4, 0.7);
  },
  omnia(g, R) {                  // the horns: eight curling out of the ring — it reaches, it makes
    ring(g, R - 24, 3.4);
    g.fillStyle = "rgba(255,255,255,0.92)";
    for (let i = 0; i < 8; i++) {
      const t = i / 8 * TAU, r0 = R - 25, r1 = R - 4, P = (a, r) => [Math.cos(a) * r, Math.sin(a) * r];
      g.beginPath();
      g.moveTo(...P(t - 0.11, r0));
      g.quadraticCurveTo(...P(t - 0.05, r0 + 16), ...P(t + 0.15, r1));
      g.quadraticCurveTo(...P(t + 0.05, r0 + 7), ...P(t + 0.11, r0));
      g.closePath(); g.fill();
    }
  },
  construct(g, R) {              // a foundation: a square laid in the ring, a stone at each corner
    ring(g, R - 4, 2, 0.75);
    const h = (R - 10) / Math.SQRT2;
    line(g, 3.4); g.strokeRect(-h, -h, h * 2, h * 2);
    line(g, 1.5, 0.7); g.strokeRect(-h + 12, -h + 12, h * 2 - 24, h * 2 - 24);
    g.fillStyle = "rgba(255,255,255,0.95)";
    for (const [x, y] of [[-h, -h], [h, -h], [-h, h], [h, h]]) g.fillRect(x - 8, y - 8, 16, 16);
  },
  treasure(g, R) {               // a cut gem, seen from above
    line(g, 3.4); poly(g, 8, R - 4, TAU / 16); g.stroke();
    line(g, 1.6, 0.8); poly(g, 8, R - 26, 0); g.stroke();
    for (let i = 0; i < 8; i++) {
      const a = TAU / 16 + i / 8 * TAU, b = i / 8 * TAU, c = (i + 1) / 8 * TAU;
      g.beginPath(); g.moveTo(Math.cos(b) * (R - 26), Math.sin(b) * (R - 26)); g.lineTo(Math.cos(a) * (R - 4), Math.sin(a) * (R - 4)); g.lineTo(Math.cos(c) * (R - 26), Math.sin(c) * (R - 26)); g.stroke();
    }
  },
  contract(g, R) {               // a chain: links around the ring, each one through the next
    ring(g, R - 30, 1.6, 0.7);
    const n = 18, rr = R - 14;
    for (let i = 0; i < n; i++) {
      const t = i / n * TAU;
      g.save(); g.translate(Math.cos(t) * rr, Math.sin(t) * rr); g.rotate(t + Math.PI / 2);
      line(g, i % 2 ? 2.2 : 3.2, i % 2 ? 0.75 : 0.95);
      g.beginPath();
      if (i % 2) g.ellipse(0, 0, 4, 9, Math.PI / 2, 0, TAU); else g.ellipse(0, 0, 11, 5.5, 0, 0, TAU);
      g.stroke(); g.restore();
    }
  },
};

const HEART_DRAW = {
  self(g) { ring(g, 16, 2.4); g.fillStyle = "rgba(255,255,255,0.95)"; g.beginPath(); g.arc(0, 0, 7, 0, TAU); g.fill(); },
  area(g) { line(g, 3); poly(g, 3, 20, -Math.PI / 2); g.stroke(); g.fillStyle = "rgba(255,255,255,0.95)"; g.beginPath(); g.arc(0, 0, 3.5, 0, TAU); g.fill(); },
  global(g) {
    line(g, 2.6); g.beginPath();
    for (let i = 0; i <= 16; i++) { const t = i / 16 * TAU - Math.PI / 2, r = i % 2 ? 9 : 24; i ? g.lineTo(Math.cos(t) * r, Math.sin(t) * r) : g.moveTo(Math.cos(t) * r, Math.sin(t) * r); }
    g.stroke();
  },
};

// rim + heart: white line art, tinted by the caster's colour where it is used
export function drawSealBody(g, size, seal) {
  const c = size / 2;
  g.save(); g.translate(c, c); g.scale(size / 256, size / 256);   // drawn at 256, any size
  g.shadowColor = "#fff"; g.shadowBlur = 7;
  (RIM_DRAW[seal.rim] || RIM_DRAW.impera)(g, 122);
  (HEART_DRAW[seal.heart] || HEART_DRAW.self)(g);
  g.restore();
}

// the Four: their own colours, at their canon places (top of the canvas = top of the screen)
export function drawSealFour(g, size, seal) {
  const c = size / 2;
  g.save(); g.translate(c, c); g.scale(size / 256, size / 256);
  const D = 64;                                      // how far out the points sit (inside every rim)
  for (const [k, f] of Object.entries(FOUR)) {
    const x = f.at[0] * D, y = f.at[1] * D, on = seal.four[k];
    if (on) {
      g.strokeStyle = f.core; g.lineWidth = 3; g.shadowColor = f.glow; g.shadowBlur = 8; g.globalAlpha = 0.7;
      g.beginPath(); g.moveTo(f.at[0] * 26, f.at[1] * 26); g.lineTo(x - f.at[0] * 12, y - f.at[1] * 12); g.stroke();
      g.globalAlpha = 1; g.shadowBlur = 14;
      g.fillStyle = f.core; g.beginPath(); g.moveTo(x, y - 13); g.lineTo(x + 10, y); g.lineTo(x, y + 13); g.lineTo(x - 10, y); g.closePath(); g.fill();
      g.shadowBlur = 0; g.fillStyle = f.glow; g.beginPath(); g.moveTo(x, y - 6); g.lineTo(x + 4.5, y); g.lineTo(x, y + 6); g.lineTo(x - 4.5, y); g.closePath(); g.fill();
    } else {
      // unused: a faint pale outline (a deep colour vanishes under additive blending)
      g.shadowBlur = 0; g.globalAlpha = 0.35; g.strokeStyle = "#d8d0e8"; g.lineWidth = 2;
      g.beginPath(); g.moveTo(x, y - 7); g.lineTo(x + 5.5, y); g.lineTo(x, y + 7); g.lineTo(x - 5.5, y); g.closePath(); g.stroke();
      g.globalAlpha = 1;
    }
  }
  g.restore();
}
